import React from 'react';
import { withState, withHandlers } from 'recompose';
import { compose } from '@reduxjs/toolkit';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import PropTypes from 'prop-types';
import { Button, InputLabel } from '@mui/material';
import { sendSmConfigImage, logState } from 'src/reducers/parameters';

import FileUploaderBtn from '../common/FileUploaderBtn';
import utils from 'src/utils/utils';
import getApiCaller from 'src/utils/apiClientCaller';
import { useAlert } from 'src/hooks';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles()(theme => ({
  loadedImgOuter: {
    ...theme.typography.caption,
    width: '200px',
    color: theme.palette.common.black,
    textAlign: 'center',
    padding: '2px',
  },
  loadedImgInner: {
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    height: '100px',
    width: '200px',
    backgroundPosition: 'center',
  },
}));

const LoadedImg = ({ file, dataUrl }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  return (
    <div className={classes.loadedImgOuter}>
      <div
        className={classes.loadedImgInner}
        style={{
          backgroundImage: `url(${dataUrl})`,
        }}
      />
      <span>
        {file.name} ({t('file_size_short', { fileSize: (file.size / 1000).toFixed(1) })})
      </span>
    </div>
  );
};

LoadedImg.propTypes = {
  dataUrl: PropTypes.string.isRequired,
  file: PropTypes.object.isRequired,
};

const useFieldStyles = makeStyles()(theme => ({
  savedImgCtn: {
    backgroundSize: 'contain',

    backgroundRepeat: 'no-repeat',
    height: '100px',
    width: '200px',
    backgroundPosition: 'center',
  },
  deleteBtn: {
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.main,
    '&:hover': {
      backgroundColor: theme.palette.warning.light,
      opacity: 0.9,
    },
  },
  button: {
    margin: theme.spacing(0.5),
  },
}));

const SmConfigImageField = ({
  label,
  storedFileId,
  dataUrl,
  onFileChange,
  paramKey,
  doSendSmConfigImage,
  onFieldChange,
  doGetApiCaller,
}) => {
  const { classes } = useFieldStyles();
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  return (
    <div
      style={{
        margin: '24px 24px 0',
        textAlign: 'left',
      }}
    >
      <InputLabel shrink style={{ display: 'inline-flex', fontWeight: 500 }}>
        {label}
      </InputLabel>
      {!storedFileId && (
        <FileUploaderBtn
          selectFileLabel={t('smartmessaging.config.imagefield.selectFile')}
          sendFileLabel={t('smartmessaging.config.imagefield.send')}
          cancelLabel={t('smartmessaging.config.imagefield.cancel')}
          displayCmp={file => <LoadedImg file={file} dataUrl={dataUrl} />}
          accept="image/*"
          onFileChange={onFileChange}
          onSend={file => {
            doSendSmConfigImage(paramKey, file, showAlert);
          }}
        />
      )}
      {storedFileId && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            className={classes.savedImgCtn}
            style={{
              backgroundImage: `url(${utils.link(storedFileId)})`,
            }}
          />

          <Button
            className={classes.deleteBtn}
            onClick={e => {
              e.preventDefault();
              onFieldChange(paramKey, null);
            }}
          >
            {t('smartmessaging.config.imagefield.delete')}
          </Button>
          <Button
            className={classes.button}
            color="primary"
            variant="contained"
            onClick={() => {
              utils.downloadStoredFile(storedFileId, doGetApiCaller(showAlert));
            }}
          >
            {t('smartmessaging.config.imagefield.download')}
          </Button>
        </div>
      )}
    </div>
  );
};

SmConfigImageField.propTypes = {
  storedFileId: PropTypes.number,
  dataUrl: PropTypes.string,
  onFileChange: PropTypes.func.isRequired,
  label: PropTypes.any,
  paramKey: PropTypes.string.isRequired,
  doSendSmConfigImage: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  doGetApiCaller: PropTypes.func.isRequired,
};

SmConfigImageField.defaultProps = {
  storedFileId: null,
  dataUrl: '',
  label: '',
};

const mapStateToProps = () => ({});

const actionCreators = {
  doSendSmConfigImage: sendSmConfigImage,
  onFieldChange: (key, value) => ({ type: 'ON_SM_CFG_FIELD_CHANGE', value: { key, value } }),
  doLogState: logState,
  doGetApiCaller: getApiCaller,
};

const handlers = {
  onFileChange: props => (e, pFile) => {
    const { setDataUrl } = props;
    if (!e && !pFile) {
      setDataUrl('');
      return false;
    }
    const file = pFile || e.target.files[0];
    if (!file) {
      setDataUrl('');
      return false;
    }
    const pattern = /image-*/;
    const reader = new FileReader();

    if (!file.type.match(pattern)) {
      return false;
    }

    reader.onload = async () => {
      setDataUrl(reader.result);
    };

    reader.readAsDataURL(file);
    return true;
  },
};

export default compose(
  connect(mapStateToProps, actionCreators),
  withState('dataUrl', 'setDataUrl', () => ''),
  withHandlers(handlers)
)(SmConfigImageField);
